
import React, { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Mail,
  MapPin,
  MessageCircle,
  Navigation,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import { api } from "../lib/api";

const initialFormData = {
  name: "",
  phone: "",
  email: "",
  inquiryType: "Inquiry about Admission",
  message: "",
  agreeToTerms: false,
  receiveUpdates: false,
};

const schoolAddress =
  "St. Xavier's Public School, Bela, Cholapur, Varanasi - 221101";

const schoolEmail = "st.xavierbelavns@gmail.com";

const schoolPhones = [
  {
    label: "+91 86010 17877",
    value: "+918601017877",
  },
  {
    label: "+91 94507 85369",
    value: "+919450785369",
  },
];

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-2.5 text-[13px] text-slate-800 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:ring-2 focus:ring-red-100";

export default function ContactSection() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const mapQuery = encodeURIComponent(schoolAddress);

  const mapUrl =
    `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

  function handleInputChange(event) {
    const { name, value, type, checked } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (submitStatus) {
      setSubmitStatus(null);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isSubmitting) return;

    if (!formData.agreeToTerms) {
      setSubmitStatus("terms");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await api.submitEnquiry({
        fullName: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        subject: formData.inquiryType,
        message: formData.message.trim(),
        receiveUpdates: formData.receiveUpdates,
        agreeTerms: formData.agreeToTerms,
        source: "contact",
      });

      setSubmitStatus("success");
      setFormData({ ...initialFormData });
    } catch (error) {
      console.error("Enquiry submission failed:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="relative isolate w-full overflow-hidden bg-[#FFFCF9] py-8 sm:py-9 lg:py-10">

      {/* Background Decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-0 h-72 w-72 rounded-full bg-red-100/50 blur-3xl" />

        <div className="absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-amber-100/50 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-4 sm:px-7 lg:px-10 xl:px-16">

        {/* SECTION HEADING */}
        <div className="mb-7 text-center">

          <span className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-white px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.16em] text-red-800 shadow-sm">
            <Sparkles size={13} className="text-amber-500" />
            We're Here to Help
          </span>

          <h2 className="mt-3 font-serif text-[30px] font-black leading-tight tracking-tight text-slate-950 sm:text-[38px] lg:text-[44px]">
            Let's Get{" "}
            <span className="relative inline-block text-red-800">
              Connected
              <span className="absolute -bottom-1.5 left-0 h-[3px] w-full rounded-full bg-gradient-to-r from-red-700 to-amber-400" />
            </span>
          </h2>

          <p className="mx-auto mt-3 max-w-2xl text-[13px] leading-6 text-slate-500">
            Have a question about admissions or school
            activities? We're here to help.
          </p>
        </div>

        {/* TWO COLUMN LAYOUT */}
        <div className="grid items-stretch gap-5 lg:grid-cols-[0.92fr_1.08fr] lg:gap-7">

          {/* LEFT - CONTACT DETAILS */}
          <div className="flex flex-col gap-4">

            {/* CONTACT INFORMATION CARD */}
            <div className="overflow-hidden rounded-[22px] border border-red-100 bg-white shadow-[0_12px_38px_rgba(70,25,25,0.06)]">

              <div className="relative overflow-hidden bg-gradient-to-r from-red-900 to-red-700 px-5 py-4 text-white">

                <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full border-[18px] border-white/10" />

                <div className="relative flex items-center gap-3">

                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                    <MessageCircle size={20} />
                  </div>

                  <div>
                    <h3 className="font-serif text-lg font-bold">
                      Contact Information
                    </h3>

                    <p className="mt-0.5 text-[11px] text-white/75">
                      Connect with our school office
                    </p>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-slate-100 px-5">

                {/* PHONE */}
                <div className="flex items-start gap-4 py-4">

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
                    <Phone size={20} />
                  </div>

                  <div className="min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Call Our Office
                    </h4>

                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                      {schoolPhones.map((phone) => (
                        <a
                          key={phone.value}
                          href={`tel:${phone.value}`}
                          className="text-[13px] font-semibold text-red-800 transition hover:text-red-600"
                        >
                          {phone.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                {/* EMAIL */}
                <div className="flex items-start gap-4 py-4">

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                    <Mail size={20} />
                  </div>

                  <div className="min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Email Address
                    </h4>

                    <a
                      href={`mailto:${schoolEmail}`}
                      className="mt-1.5 block break-all text-[13px] font-semibold text-red-800 transition hover:text-red-600"
                    >
                      {schoolEmail}
                    </a>
                  </div>
                </div>

                {/* ADDRESS */}
                <div className="flex items-start gap-4 py-4">

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
                    <MapPin size={20} />
                  </div>

                  <div>
                    <h4 className="text-sm font-extrabold text-slate-900">
                      School Address
                    </h4>

                    <p className="mt-1.5 text-[13px] leading-6 text-slate-600">
                      St. Xavier&apos;s Public School,
                      Bela, Cholapur, Varanasi – 221101
                    </p>

                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-red-800 hover:text-red-600"
                    >
                      <Navigation size={14} />
                      Get Directions
                      <ArrowRight size={13} />
                    </a>
                  </div>
                </div>

                {/* SCHOOL HOURS */}
                <div className="flex items-start gap-4 py-4">

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                    <Clock3 size={20} />
                  </div>

                  <div>
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Office Hours
                    </h4>

                    <p className="mt-1.5 text-[13px] text-slate-600">
                      Monday to Saturday
                    </p>

                    <p className="mt-1 text-[13px] font-semibold text-red-800">
                      9:00 AM – 4:00 PM
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* SMALL HIGHLIGHT CARD */}
            <div className="relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-5 py-4">

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <CheckCircle2 size={20} />
                </div>

                <div>
                  <h4 className="text-sm font-bold text-slate-900">
                    Visit Our Campus
                  </h4>

                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    Contact our school office before your
                    visit to confirm availability.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT - ENQUIRY FORM */}
          <div className="overflow-hidden rounded-[22px] border border-slate-100 bg-white shadow-[0_15px_45px_rgba(30,30,30,0.07)]">

            {/* FORM HEADER */}
            <div className="relative overflow-hidden bg-gradient-to-r from-red-50 via-white to-amber-50 px-5 py-4 sm:px-6">

              <div className="flex items-center gap-3">

                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-800 text-white">
                  <Send size={20} />
                </div>

                <div>
                  <h3 className="font-serif text-lg font-bold text-slate-900 sm:text-xl">
                    Send Us a Message
                  </h3>

                  <p className="mt-1 text-[11px] text-slate-500">
                    Fill in your details and we'll get back to you.
                  </p>
                </div>
              </div>
            </div>

            {/* FORM BODY */}
            <div className="p-5 sm:p-6">

              {submitStatus === "success" && (
                <div
                  role="status"
                  className="mb-4 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-xs font-semibold text-green-800"
                >
                  <CheckCircle2 size={17} className="shrink-0" />
                  Your message has been submitted
                  successfully.
                </div>
              )}

              {submitStatus === "error" && (
                <div
                  role="alert"
                  className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700"
                >
                  Unable to send your message. Please
                  try again.
                </div>
              )}

              {submitStatus === "terms" && (
                <div
                  role="alert"
                  className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
                >
                  Please accept the Terms and Privacy
                  Policy before submitting.
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="space-y-3.5"
              >

                {/* NAME AND PHONE */}
                <div className="grid gap-3 sm:grid-cols-2">

                  <div>
                    <label
                      htmlFor="contact-name"
                      className="mb-1.5 block text-xs font-bold text-slate-700"
                    >
                      Full Name *
                    </label>

                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleInputChange}
                      autoComplete="name"
                      required
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-phone"
                      className="mb-1.5 block text-xs font-bold text-slate-700"
                    >
                      Phone Number *
                    </label>

                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      placeholder="Phone number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      autoComplete="tel"
                      required
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* EMAIL AND TYPE */}
                <div className="grid gap-3 sm:grid-cols-2">

                  <div>
                    <label
                      htmlFor="contact-email"
                      className="mb-1.5 block text-xs font-bold text-slate-700"
                    >
                      Email Address *
                    </label>

                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      placeholder="Email address"
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      required
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-subject"
                      className="mb-1.5 block text-xs font-bold text-slate-700"
                    >
                      Enquiry Type
                    </label>

                    <select
                      id="contact-subject"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      className={inputClass}
                    >
                      <option value="Inquiry about Admission">
                        Inquiry about Admission
                      </option>

                      <option value="School Visit Request">
                        School Visit Request
                      </option>

                      <option value="General Information">
                        General Information
                      </option>

                      <option value="Feedback/Suggestion">
                        Feedback/Suggestion
                      </option>
                    </select>
                  </div>
                </div>

                {/* MESSAGE */}
                <div>
                  <label
                    htmlFor="contact-message"
                    className="mb-1.5 block text-xs font-bold text-slate-700"
                  >
                    Your Message *
                  </label>

                  <textarea
                    id="contact-message"
                    name="message"
                    rows={3}
                    placeholder="How can we help you?"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className={`${inputClass} resize-none`}
                  />
                </div>

                {/* CHECKBOXES */}
                <div className="space-y-2.5">

                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      required
                      className="mt-1 h-4 w-4 shrink-0 accent-red-800"
                    />

                    <span className="text-[11px] leading-5 text-slate-600">
                      I agree to the{" "}
                      <a
                        href="/terms"
                        className="font-semibold text-red-800 hover:underline"
                      >
                        Terms &amp; Conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy-policy"
                        className="font-semibold text-red-800 hover:underline"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>

                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      name="receiveUpdates"
                      checked={formData.receiveUpdates}
                      onChange={handleInputChange}
                      className="mt-1 h-4 w-4 shrink-0 accent-red-800"
                    />

                    <span className="text-[11px] leading-5 text-slate-600">
                      I would like to receive school
                      updates via Email, SMS and WhatsApp.
                    </span>
                  </label>

                </div>

                {/* SUBMIT BUTTON */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-800 to-red-700 px-5 py-3 text-sm font-extrabold text-white shadow-md transition hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                      <ArrowRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>

                <p className="flex items-center justify-center gap-1.5 text-center text-[10px] text-slate-400">
                  <ShieldCheck size={13} />
                  Your enquiry will be shared with the school team.
                </p>

              </form>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
